/**
 * London Smoke & Cure — Njord rooms (Firestore via Cloud Run API).
 */

import { getNjordCloudRunUrl, njordAdminHeaders } from './njordConfig';
import { njordFetchErrorMessage } from './njordHttpErrors';
import { njordRoomsGetCache, njordSensorsGetCache } from './njordRegistryGetCache';

async function parseJson(res) {
    const text = await res.text();
    let data;
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`Njord API non-JSON (${res.status})`);
    }
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
}

/**
 * Normalize a **new** room/sensor code to lowercase slug (create/register).
 * Do not use this to rewrite an existing Firestore document id — use {@link njordPathSlug}.
 */
export function sanitizeNjordSlug(raw) {
    const s = String(raw || '')
        .trim()
        .toLowerCase()
        .slice(0, 128);
    if (!s || !/^[a-z0-9_-]+$/.test(s)) return '';
    return s;
}

/**
 * Preserve an existing Firestore / API path id (case-sensitive).
 * Lowercasing here caused "Room not found" when docs were created as e.g. Upstairs.
 */
export function njordPathSlug(raw) {
    const s = String(raw || '').trim().slice(0, 128);
    if (!s || !/^[a-zA-Z0-9_-]+$/.test(s)) return '';
    return s;
}

export async function fetchNjordRooms(opts = {}) {
    return njordRoomsGetCache.get(
        'rooms',
        async () => {
            const base = getNjordCloudRunUrl();
            if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
            try {
                const res = await fetch(`${base}/api/rooms`, {
                    headers: njordAdminHeaders(),
                    cache: 'no-store',
                });
                const data = await parseJson(res);
                const rooms = Array.isArray(data.rooms) ? data.rooms : [];
                return rooms
                    .filter((r) => r && typeof r === 'object' && String(r.id || '').trim())
                    .map((r) => ({ ...r, id: String(r.id).trim() }));
            } catch (err) {
                throw new Error(njordFetchErrorMessage(err, 'Could not load rooms'));
            }
        },
        { force: Boolean(opts.force) }
    );
}

/**
 * Create/update a room.
 * New rooms: pass the code entered by the operator (lowercased).
 * Existing rooms: pass `{ existing: true }` so the Firestore document id case is preserved.
 * @param {string} roomId
 * @param {{ label?: string, thresholds?: object, members?: object }} body
 * @param {{ existing?: boolean }} [opts]
 */
export async function saveNjordRoom(roomId, body, opts = {}) {
    const id = opts.existing ? njordPathSlug(roomId) : sanitizeNjordSlug(roomId);
    if (!id) throw new Error('Invalid room id');
    const base = getNjordCloudRunUrl();
    if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
    const res = await fetch(`${base}/api/rooms/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { ...njordAdminHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    njordRoomsGetCache.invalidate();
    return data;
}

/**
 * @param {string} roomId exact Firestore room document id
 * @param {string} sensorId exact Firestore / registry sensor id
 * @param {'add'|'remove'} action
 */
export async function setNjordRoomMember(roomId, sensorId, action = 'add') {
    const rid = njordPathSlug(roomId);
    const sid = njordPathSlug(sensorId);
    if (!rid || !sid) {
        throw new Error(
            !rid
                ? 'Invalid room id (letters, numbers, _ and - only)'
                : 'Invalid sensor id (letters, numbers, _ and - only — not the display name)'
        );
    }
    const base = getNjordCloudRunUrl();
    if (!base) throw new Error('REACT_APP_NJORD_CLOUD_RUN_URL is not set');
    try {
        const res = await fetch(`${base}/api/rooms/${encodeURIComponent(rid)}/members`, {
            method: 'POST',
            headers: { ...njordAdminHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ sensorId: sid, action }),
        });
        const data = await parseJson(res);
        njordRoomsGetCache.invalidate();
        njordSensorsGetCache.invalidate();
        return data;
    } catch (err) {
        throw new Error(njordFetchErrorMessage(err, 'Assignment failed'));
    }
}
